import type { PendingApprovalEntry } from '@clockwise/clients/timesheet-approvals';

/**
 * Decision-side helpers for the approvals page, split out so the
 * locked-period fan-out and the disabled-state copy are unit-testable
 * without mounting the whole route. A locked entry can no longer be
 * approved OR rejected — the server vetoes it (see
 * `TimesheetLockDecisionGuard`) — so the UI both filters locked rows out of
 * bulk decisions and disables the affordance for both actions.
 */

/** The two approver decisions these helpers word copy for. */
export type DecisionVerb = 'approve' | 'reject';

const VERB: Record<DecisionVerb, { imperative: string; past: string }> = {
  approve: { imperative: 'Approve', past: 'approved' },
  reject: { imperative: 'Reject', past: 'rejected' },
};

/** Entries whose timesheet period hasn't locked — the only ones a decision
 *  may touch (see the `locked` flag on `PendingApprovalEntry`). */
export function unlockedEntries(entries: PendingApprovalEntry[]): PendingApprovalEntry[] {
  return entries.filter((e) => !e.locked);
}

/** Decision-button title + popover copy for a bulk scope (group/sub-group)
 *  that may contain locked-period entries. `allLocked` disables the
 *  trigger; the mixed case explains that locked rows will be skipped.
 *  `description` (the reject reason prompt) is only meaningful for reject —
 *  approve has no reason popover and ignores it. */
export function decisionCopy(
  entries: PendingApprovalEntry[],
  scopeLabel: string,
  verb: DecisionVerb,
): { allLocked: boolean; title: string; description: string } {
  const { imperative, past } = VERB[verb];
  const eligible = unlockedEntries(entries).length;
  const locked = entries.length - eligible;
  if (eligible === 0) {
    return {
      allLocked: true,
      title: `All entries for ${scopeLabel} are in a locked timesheet period and can no longer be ${past}`,
      description: '',
    };
  }
  if (locked === 0) {
    return {
      allLocked: false,
      title: `${imperative} all ${entries.length} entries for ${scopeLabel}`,
      description: `${imperative} all ${entries.length} entries for ${scopeLabel}. The submitter will see this reason on every row.`,
    };
  }
  return {
    allLocked: false,
    title: `${imperative} ${eligible} of ${entries.length} entries for ${scopeLabel} (${locked} in a locked period)`,
    description: `${imperative} the ${eligible} eligible ${eligible === 1 ? 'entry' : 'entries'} for ${scopeLabel} — ${locked} ${locked === 1 ? 'entry is' : 'entries are'} in a locked timesheet period and will be skipped. The submitter will see this reason on every ${past} row.`,
  };
}
